import React from "react";

import { useForm } from "react-hook-form";

import { CollapsibleCard, Input, Button, IconTrash } from "@busbud/horizon";

import { ExpiryInputV2 } from "@app/components/expiry-input/expiry-input-v2";
import { useAppContext } from "@app/helpers/hooks";
import type { CreditCard } from "@app/types/napi-types";

import type { SubmitHandler } from "react-hook-form";

import { CardBrandImage } from "./card-brand-image";
import {
  clickedUpdateCreditCard,
  updatedCreditCard
} from "../../../../../../tracking/account-tracking";

const getCardNumberPlaceholder = (last_four: string) =>
  `**** **** **** ${last_four}`;

type CreditCardInformation = "expiry_month" | "expiry_year" | "holder_name";

interface Props {
  user_id: number;
  credit_card: CreditCard;
  onDelete: () => void;
  onError: (err: string | null) => void;
  onSuccess: () => void;
}

export const PaymentMethod: React.FC<Props> = ({
  credit_card,
  user_id,
  onDelete,
  onSuccess,
  onError
}) => {
  const { api, tracker, translator } = useAppContext();
  const methods = useForm({
    defaultValues: {
      // Cast so that "03" is passed as "3" instead
      expiry_month: Number(credit_card.expiry_month).toString(),
      expiry_year: credit_card.expiry_year,
      holder_name: credit_card.holder_name
    }
  });
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: {
      touchedFields,
      errors,
      isDirty,
      isValid,
      isSubmitting,
      defaultValues
    }
  } = methods;

  const onSubmit: SubmitHandler<{}> = async values => {
    tracker.track(clickedUpdateCreditCard());

    try {
      await api.updateCreditCard(user_id, { ...credit_card, ...values });
      reset(values);
      tracker.track(updatedCreditCard());
      onSuccess();
    } catch (err) {
      if (err.type === "TransactionCardInfoIncorrect") {
        onError(translator.t("!accounts.settings.cc-incorrect-information"));
      } else {
        onError(translator.t("!accounts.settings.cc-update-error"));
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <CollapsibleCard
        key={credit_card.id}
        as="section"
        renderHeader={headerProps => (
          <CollapsibleCard.Header
            {...headerProps}
            buttonAriaLabel={translator.t("!accounts.settings.expand-content")}
            slotStart={<CardBrandImage credit_card_brand={credit_card.brand} />}
          >
            <CollapsibleCard.Title as="h2" className="first-letter:uppercase">
              {translator.t("!accounts.settings.cc-ending-in", {
                credit_card_brand: credit_card.brand,
                last_four_digits: credit_card.last_four
              })}
            </CollapsibleCard.Title>
          </CollapsibleCard.Header>
        )}
        isInitiallyExpanded={false}
      >
        <CollapsibleCard.Body>
          <div className="flex flex-col gap-150">
            <Input
              isDisabled
              value={getCardNumberPlaceholder(credit_card.last_four)}
              labelText={translator.t("!checkout.payment.card-number")}
              className="w-full"
              data-testid="card-number"
              slotEnd={<CardBrandImage credit_card_brand={credit_card.brand} />}
            />
            <ExpiryInputV2
              isDisabled
              has_error={false}
              field_key="expiry_input"
              default_month_value={defaultValues?.expiry_month || ""}
              default_year_value={defaultValues?.expiry_year || ""}
              onInputChange={(_value, e) => {
                const { target } = e;
                setValue(target.name as CreditCardInformation, target.value, {
                  shouldDirty: true
                });
              }}
            />
            <Input
              type="text"
              labelText={translator.t("!checkout.payment.cardholder-name")}
              id="holder_name"
              data-testid="cardholder"
              isDisabled
              isInvalid={
                !!touchedFields.holder_name && Boolean(errors.holder_name)
              }
              errorText={errors.holder_name?.message}
              {...register("holder_name")}
            />

            {!!isDirty && (
              <Button
                id="submit"
                isSubmit
                isDisabled={isSubmitting || !isValid}
                variant="contained"
                size="lg"
                data-testid="submit"
                className="w-full"
              >
                {translator.t("!accounts.settings.save")}
              </Button>
            )}

            <Button
              variant="contained"
              appearance="destructive"
              data-testid="delete"
              iconStart={<IconTrash size="md" />}
              className="w-full"
              size="lg"
              onClick={onDelete}
            >
              {translator.t("!accounts.settings.delete-card")}
            </Button>
          </div>
        </CollapsibleCard.Body>
      </CollapsibleCard>
    </form>
  );
};
